import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Member } from '../../../../../../api/groups';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import { useResultsDataContext } from '../../ResultsDataContext';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import { buildRelativePath } from '../../../../../../hooks/useRoutes';
import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';

export type GroupAndSelectedMemberId = [string, string];

export function createLinkWithMembersToFilter(members: string[], groupIds: string[], language: SupportedUserLanguage) {
  const url = buildRelativePath('MATERIAL.RESULTS.BY_STUDENT', language);

  const search = new URLSearchParams(window.location.search);

  search.set(FilterSearchParam.Groups, groupIds.join(','));
  search.set(FilterSearchParam.Members, members.join(','));

  return `../${url}?${search.toString()}`;
}

function processConcatenatedMemberIds(
  concatenatedMemberIds: string | null,
): Record<string, GroupAndSelectedMemberId[]> | null {
  if (!concatenatedMemberIds) return null;

  const groupIdAndMemberId = concatenatedMemberIds
    .split(',')
    .map(concatenatedId => concatenatedId.split(';')) as GroupAndSelectedMemberId[];

  return groupBy(groupIdAndMemberId, ([groupId]) => groupId);
}

export function useSelectedMembers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { members: membersPerGroup } = useResultsDataContext();

  const concatenatedMemberIds = searchParams.get(FilterSearchParam.Members);
  const spMemberIdsPerGroup = processConcatenatedMemberIds(concatenatedMemberIds);

  const selectedMembersPerGroup = useMemo(() => {
    if (!spMemberIdsPerGroup) return membersPerGroup;

    return mapValues(spMemberIdsPerGroup, (groupAndMemberIds: GroupAndSelectedMemberId[], groupId: string) => {
      const memberIds = groupAndMemberIds.map(([, memberId]) => memberId);

      return (membersPerGroup[groupId] ?? []).filter(member => memberIds.includes(member.id));
    }) as Record<string, Member[]>;
  }, [membersPerGroup, spMemberIdsPerGroup]);

  const setSelectedMembers = useCallback(
    (members: string[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        if (members.length === 0) {
          newParams.delete(FilterSearchParam.Members);
          return newParams;
        }

        newParams.set(FilterSearchParam.Members, members.join(','));
        return newParams;
      });
    },
    [setSearchParams],
  );

  return { selectedMembers: selectedMembersPerGroup, setSelectedMembers, spMemberIdsPerGroup };
}
